// API Service with error handling and retry logic
import { 
  API_CONFIG, 
  SECURITY_CONFIG, 
  getApiUrl, 
  getSecurityHeaders, 
  handleCorsError, 
  debugApiConfig 
} from '../config/apiConfig';

class ApiService {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    
    // Debug API configuration in development
    debugApiConfig();
  }

  async makeRequest(endpoint, options = {}) {
    const url = getApiUrl(endpoint);
    const securityHeaders = getSecurityHeaders(options.headers);
    
    const defaultOptions = {
      method: 'GET',
      headers: securityHeaders,
      timeout: SECURITY_CONFIG.API.TIMEOUT,
      ...options
    };

    try {
      if (SECURITY_CONFIG.ENVIRONMENT.IS_DEVELOPMENT) {
        console.log('Making API request to:', url);
        console.log('Request options:', defaultOptions);
      }
      
      const response = await fetch(url, defaultOptions);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          headers: Object.fromEntries(response.headers.entries()),
          body: errorText
        });
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      
      const data = await response.json();
      if (SECURITY_CONFIG.ENVIRONMENT.IS_DEVELOPMENT) {
        console.log('API Success Response:', data);
      }
      return data;
    } catch (error) {
      console.error('API Request failed:', {
        url,
        error: error.message,
        stack: error.stack
      });
      
      // Use the CORS error handler
      const corsErrorInfo = handleCorsError(error, url);
      
      if (corsErrorInfo.isCorsError) {
        throw new Error(corsErrorInfo.userMessage);
      }
      
      throw error;
    }
  }

  // GET request
  async get(endpoint, options = {}) {
    return this.makeRequest(endpoint, { method: 'GET', ...options });
  }

  // POST request
  async post(endpoint, data, options = {}) {
    return this.makeRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options
    });
  }

  // PUT request
  async put(endpoint, data, options = {}) {
    return this.makeRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
      ...options
    });
  }

  // DELETE request
  async delete(endpoint, options = {}) {
    return this.makeRequest(endpoint, { method: 'DELETE', ...options });
  }

  // Retry functionality for failed requests
  async retryRequest(endpoint, options = {}, attempt = 1) {
    try {
      return await this.makeRequest(endpoint, options);
    } catch (error) {
      if (attempt < SECURITY_CONFIG.API.RETRY.MAX_ATTEMPTS) {
        console.log(`Retrying request (attempt ${attempt + 1})...`);
        await new Promise(resolve => setTimeout(resolve, SECURITY_CONFIG.API.RETRY.DELAY));
        return this.retryRequest(endpoint, options, attempt + 1);
      }
      throw error;
    }
  }
}

export const apiService = new ApiService();
export default apiService;
